import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Table from 'apollo-react/components/Table';
import React, { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { getFormDataObjectList } from './../DatasourceModal/utils';
import {
  ActionCell,
  DataSourceValuesList,
  EditableCellOpenEntity,
  EntityColumnsList,
  ReadOnlyColumn,
} from './columns';
import { ExpandableComponent } from './ExpandableComponent';
import { useFormSelector } from '../../../../../custom-hooks';
import {
  CONTAINER_CONTROLS_WITH_MAPPING,
  DATALOOKUP_TYPE_EXTERNAL_API,
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
} from '../../../../constants/form-manager.constants';
import { getEntityDefinition, getTranslations } from '../../../../selectors/common.selectors';
import { getApiConfigurationVariables } from '../../../../selectors/editor.selectors';
import { getModelById, getModelByProp } from '../../../../selectors/form-manager.selectors';
import { getDataSourceFieldList, modifyVariables } from '../../../../utils/editor.utils';

const styles = {
  root: {
    overflow: 'hidden',
    '& [class*="Table-tableWrapper"]': {
      overflow: 'hidden',
    },
    '& [class*="MuiToolbar-root"]': {
      display: 'flex',
      justifyContent: 'flex-start',
    },
    '& [class*="MuiToolbar-root"] div': {
      paddingRight: 20,
    },
    '& [class*="MuiTableHead-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="MuiTableBody-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="InternalTableBody-editMode"]': {
      backgroundColor: 'white',
      borderTop: 'none !important',
      borderBottom: 'none !important',
    },
    '& [class*="EmptyRow-middleAlign"]': {
      height: '90px !important',
    },

    '& [class*="EmptyRow-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
      minHeight: '239px',
    },
    '& [class*="MuiFormControl-marginNormal"]': {
      marginTop: '0px',
    },
  },
};

const mappingColumns = [
  {
    header: 'To',
    accessor: 'translatedUpdateTo',
    customCell: EditableCellOpenEntity,
  },
  {
    header: 'From',
    accessor: 'translatedUpdateFrom',
    customCell: DataSourceValuesList,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
  },
];

const excludedColumnsMappingColumns = [
  {
    header: 'To',
    accessor: 'translatedUpdateTo',
    customCell: EditableCellOpenEntity,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
  },
];

const readOnlyColumns = [
  {
    header: 'To',
    accessor: 'translatedUpdateTo',
    customCell: ReadOnlyColumn,
    width: '50%',
  },
  {
    header: 'From',
    accessor: 'translatedUpdateFrom',
    customCell: ReadOnlyColumn,
    width: '50%',
  },
];

const ssModalMappingColumns = [
  {
    header: 'To',
    accessor: 'translatedUpdateTo',
    customCell: EntityColumnsList,
  },
  {
    header: 'From',
    accessor: 'translatedUpdateFrom',
    customCell: EntityColumnsList,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
  },
];

const MappingModal = ({
  name,
  controlId,
  currentControlModel,
  currentControlMapping,
  setCurrentControlMapping,
  isReadOnly,
  dataSources,
}) => {
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [rows, setRows] = useState([]);
  const [fieldList, setFieldList] = useState([]);
  const [expandedRow, setExpandedRows] = useState([]);
  const [currentKeyTemp, setCurrentKeyTemp] = useState('');

  const { controlType, targetId, dataObject: dataObjectProp, isNewAutogeneratedZoom } =
    useFormSelector(getModelById, { id: controlId }) ?? {};
  const targetControlId = useFormSelector(getModelByProp, {
    prop: FORM_MODEL_ATTRIBUTES.UNIQUE_IDENTIFIER,
    value: targetId,
  });
  const { entityName: targetEntityName } =
    useFormSelector(getModelById, { id: targetControlId }) ?? {};
  const translations = useFormSelector(getTranslations);
  const entityDefinition = useFormSelector(getEntityDefinition);
  const inboundVariables = useFormSelector(getApiConfigurationVariables, {
    configurationName: currentControlModel.dataObject,
    dataLookUpType: currentControlModel.dataLookUpType,
    field: 'inboundVariables',
  });
  const propValue = currentControlMapping;
  const dataObject = currentControlModel.dataObject;
  const controlDataLookUpType = currentControlModel.dataLookUpType;
  const selectedDataSource = currentControlModel?.dataSourceKey;
  const dataSourceFields = dataSources[selectedDataSource]?.columnList;
  const { Entity: entityTranslations } = translations;

  const dataSourceLookUpType =
    currentControlModel.controlType === SS_CONTROL_TYPE.GRID
      ? dataSources[selectedDataSource]?.dataSourceTypeStr
      : controlDataLookUpType;

  const dataSourceObject =
    currentControlModel.controlType === SS_CONTROL_TYPE.GRID
      ? dataSources[selectedDataSource]?.dataSourceName
      : dataObject;

  const formDataObjectList = useMemo(
    () => getFormDataObjectList({ entityDefinition, entityTranslations }),
    [entityDefinition, entityTranslations]
  );

  const targetEntityColumnsList = useMemo(
    () => formDataObjectList.find((item) => item.value === targetEntityName)?.fields ?? [],
    [formDataObjectList, targetEntityName]
  );

  const sourceEntityColumnsList = useMemo(
    () => formDataObjectList.find((item) => item.value === dataObjectProp)?.fields ?? [],
    [formDataObjectList, dataObjectProp]
  );

  React.useEffect(() => {
    if (currentControlModel.dataLookUpType !== DATALOOKUP_TYPE_EXTERNAL_API) {
      getDataSourceFieldList({
        dataLookupType: dataSourceLookUpType,
        dataObject: dataSourceObject,
        formDataObjectList,
      }).then((res) => {
        let fieldToDisplay = res;
        if (CONTAINER_CONTROLS_WITH_MAPPING.includes(currentControlModel.controlType)) {
          fieldToDisplay = fieldToDisplay.filter(({ value }) => dataSourceFields?.includes(value));
        }
        setFieldList(fieldToDisplay);
      });
    } else {
      setFieldList(modifyVariables(inboundVariables));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (propValue) {
      const modifyFetchedRows = propValue?.map((item) => {
        const rowId = `${uuidv4()}`;
        return {
          ...item,
          id: rowId,
        };
      });
      setRows(modifyFetchedRows ? modifyFetchedRows : []);
    }
  }, [propValue]);

  const handleToggleRow = (id, currentKeyTemp) => {
    setCurrentKeyTemp(currentKeyTemp);
    setExpandedRows((expandedRows) =>
      expandedRows.includes(id)
        ? expandedRows.filter((Id) => Id !== id)
        : [...expandedRows, id, currentKeyTemp]
    );
  };

  const editRow = (id, value, storeUpdate, isDelete) => {
    let currentRows = rows.map((row) => (row.id === id ? value : row));

    if (!isDelete) {
      currentRows = rows.map((row) => (row.id === id ? value : row));
      if (storeUpdate) {
        updateLocalState(currentRows);
      }
    } else {
      currentRows = rows.filter((row) => row.id !== id);
      updateLocalState(currentRows);
    }
    setRows(currentRows);
  };

  const addRow = () => {
    const rowId = `${uuidv4()}`;
    const newMapping = {
      id: rowId,
      source: 'property',
      updateFrom: '',
      translatedUpdateFrom: '',
      updateTo: '',
      updateToType: '',
      translatedUpdateTo: '',
    };
    const newRows = [...rows, newMapping];
    setCurrentControlMapping(newRows);
  };

  const updateLocalState = (newRowsTemp) => {
    const newRows = newRowsTemp || rows;
    if (controlId && name) {
      const newValue = newRows.map((item) => ({
        id: uuidv4(),
        updateFrom: item.updateFrom,
        source: item.source,
        translatedUpdateFrom: item.translatedUpdateFrom,
        updateToType: item.updateToType,
        updateTo: item.updateTo,
        translatedUpdateTo: item.translatedUpdateTo,
      }));
      setCurrentControlMapping(newValue);
      setRows(newValue);
    }
  };

  const CustomHeader = () => {
    const buttonText =
      name === FORM_MODEL_ATTRIBUTES.EXCLUDED_COLUMNS_MAPPING_LIST ? 'Add fields' : 'Add Mapping';
    return (
      <div>
        <Button variant="text" size="small" onClick={addRow}>
          {buttonText}
        </Button>
      </div>
    );
  };

  return (
    <div className={classes.root}>
      <Table
        title="Mapping List"
        columns={
          controlType === SS_CONTROL_TYPE.MODAL
            ? ssModalMappingColumns
            : isNewAutogeneratedZoom
            ? mappingColumns
            : isReadOnly
            ? readOnlyColumns
            : name === FORM_MODEL_ATTRIBUTES.EXCLUDED_COLUMNS_MAPPING_LIST
            ? excludedColumnsMappingColumns
            : mappingColumns
        }
        rows={rows.map((row) => ({
          ...row,
          rows,
          key: row.id,
          currentKeyTemp,
          editMode: !isReadOnly,
          expanded: expandedRow.includes(row.id),
          translations: translations.Entity,
          editRow,
          handleToggleRow,
          classes,
          fieldList,
          entityDefinition,
          targetEntityColumnsList,
          sourceEntityColumnsList,
          targetEntityName,
          controlType,
        }))}
        initialSortOrder="asc"
        hidePagination
        CustomHeader={!isReadOnly && CustomHeader}
        ExpandableComponent={ExpandableComponent}
      />
    </div>
  );
};

export default MappingModal;
